import { faDownload, faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import { ClubMember } from "components/ClubMemberManagement/types";
import { Column, DataTable, DataTableContainer, TableActionButton } from "components/DataTable";
import { LetterForm } from "components/LetterManagement/LetterForm";
import { Letter } from "components/LetterManagement/types";
import { useDownload } from "hooks";
import { useApi } from "../../../../hooks/useApi";
import { useModal } from "hooks/useModal";
import moment from "moment";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useConfirmedCallback } from "../../../../hooks/useConfirmedCallback";

interface Props {
    clubMember: ClubMember;
}

export const LetterList = ({ clubMember }: Props) => {
    const { t } = useTranslation('member-management');
    const [setModal] = useModal();
    const list = useRef(null);
    const api = useApi();
    const download = useDownload();

    const handleDownload = (letter: Letter) =>
        api({
            url: `letter/${letter.id}/download`,
            responseType: "blob",
            onSuccess: (data) => download(data, t('clubMember.preview.letters.downloadFilename', { id: letter.id }))
        });

    const handleDelete = useConfirmedCallback((letter: Letter) =>
        api({
            url: `letter/${letter.id}`,
            method: "DELETE",
            onSuccess: () => list.current?.reload()
        }), {
        message: t('clubMember.preview.letters.deleteConfirm', { name: clubMember.name })
    });

    const showEditForm = (letter: Letter) =>
        setModal({
            title: t('clubMember.preview.letters.editTitle'),
            content: <LetterForm letterId={letter.id} />,
            show: true,
            size: 50
        });

    const columns: Column[] = [
        {
            name: t('clubMember.preview.letters.columns.subject'),
            uid: "subject"
        },
        {
            name: t('clubMember.preview.letters.columns.date'),
            uid: "date",
            renderer: (value) => (value ? moment(value).format("ll") : "")
        },
        { name: t('clubMember.preview.letters.columns.options'), uid: "actions", align: "end" }
    ];

    return (
        <DataTableContainer>
            <DataTable
                flush
                hideSearchBar
                asyncListRef={list}
                tableProps={{
                    showSelectionCheckboxes: false,
                    sticked: true,
                    compact: false
                }}
                fetchUrl={`club-member/${clubMember.id}/letters`}
                columns={columns}
                rowActions={(item) => (
                    <TableActionButton.Container>
                        <TableActionButton
                            icon={faDownload}
                            tooltip={t('clubMember.preview.letters.download')}
                            onPress={() => handleDownload(item)}
                        />
                        <TableActionButton icon={faEdit} color="warning" tooltip={t('clubMember.preview.letters.edit')}
                            onPress={() => showEditForm(item)}
                        />
                        <TableActionButton icon={faTrash} color="error" tooltip={t('clubMember.preview.letters.delete')}
                            onPress={() => handleDelete(item)}
                        />
                    </TableActionButton.Container>
                )}
            />
        </DataTableContainer>
    );
};
